/**
 * Where someone lands when they have not asked for a particular page.
 *
 * Defined once because three places had their own answer and they disagreed: the OTP
 * screen sent everyone to /sales/dashboard, the guest guard sent super admins to
 * /admin/dashboard and everyone else to /manage-workspaces, and the client page used
 * /sales/dashboard as a fallback. /sales/dashboard renders ComingSoon, so the most
 * common of those three landed on a placeholder saying the module is under development.
 *
 * The assistant is the right default: it opens on what needs you today rather than on
 * one module's dashboard.
 */
export const POST_LOGIN_PATH = '/personal-assistant/assistant'

/**
 * The page someone asked for before being sent to sign in, if it is safe to go back to.
 *
 * A link shared in Slack - a ticket, one comment on it - used to land a signed-out
 * colleague on the default page after signing in, with the thing they were sent lost.
 * Only an in-app path is honoured: it must start with one "/", so "//evil.test" and
 * "https://..." fall back to the default, and a path back into /login would loop.
 */
export function postLoginPath(requested) {
    const path = Array.isArray(requested) ? requested[0] : requested
    if (typeof path !== 'string' || !path.startsWith('/') || path.startsWith('//') || path.startsWith('/\\')) return POST_LOGIN_PATH
    if (path === '/login' || path.startsWith('/login/') || path.startsWith('/login?')) return POST_LOGIN_PATH
    return path
}

/** The bare address, which has no page of its own: it opens where a fresh sign-in lands. */
export const HOME_ROUTE = { path: '/', redirect: POST_LOGIN_PATH }

/**
 * Where the guard sends someone signed out: /login, carrying the page they asked for. The
 * home address and the default page carry nothing, since signing in lands there anyway.
 */
export function signInLocation(fullPath) {
    return fullPath && fullPath !== '/' && fullPath !== POST_LOGIN_PATH
        ? { path: '/login', query: { redirect: fullPath } }
        : { path: '/login' }
}
